import { useMemo, useState } from "react";

import { CheckBoxFilters } from "@vericus/cadmus-common";
import { Text } from "@vericus/cadmus-ui";

import * as Sentry from "@sentry/react";

import insightsImg from "@/assets/illustrations/person-with-telescope.png";
import { getActiveMembers } from "@/features/group/utils";
import { GroupFragment } from "@/generated/graphql";

import { ActivityCharts } from "./components/activity-charts";
import { DateRangeFilter } from "./components/date-range-filter";
import { EngagementCharts } from "./components/engagement-charts";
import { IssueSummaryChart } from "./components/issue-summary-chart";
import * as styles from "./insights-page.css";
import { DateRange } from "./types";

interface GroupInsightsPageProps {
  assessmentId: string;
  workId: string;
  group: GroupFragment;
}
/**
 * Displays insights for a group assessment.
 *
 * This page will display insights based on the selected groups or all groups
 * if no groups are selected.
 * It shows:
 * - Task summary - number of tasks in progress, on track, overdue, and total
 * - Sentiment check - sentiment check results for the selected groups
 * - Comments and replies - number of comments and replies for the selected groups
 * - Activity report - word count, paste ratio, sessions, citations for the selected groups
 */
export function InsightsPage(props: GroupInsightsPageProps) {
  const { assessmentId, workId, group } = props;

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>([
    null,
    null,
  ]);

  const members = useMemo(() => getActiveMembers(group), [group]);

  const selectedMembers = useMemo(() => {
    if (selectedFilters.length === 0) {
      return members;
    }
    return members.filter((m) => selectedFilters.includes(m.id));
  }, [members, selectedFilters]);

  return (
    <>
      <div className={styles.container}>
        <div>
          <CheckBoxFilters
            title="Members"
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            filters={members}
          />
        </div>
        <div className={styles.rightContainer}>
          <Section title="Tasks Summary">
            <IssueSummaryChart
              assessmentId={assessmentId}
              workId={workId}
              studentIds={selectedFilters}
              users={selectedMembers}
            />
          </Section>

          <Section
            title="Activity report"
            rightHeader={
              <DateRangeFilter
                label="Date Ranges"
                className={styles.dateRangeFilter}
                onDateRangeChange={setSelectedDateRange}
                selectedDateRange={selectedDateRange}
              />
            }
          >
            <ActivityCharts
              assessmentId={assessmentId}
              workId={workId}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              members={selectedMembers}
            />
          </Section>
          <Section title="Comments and Replies">
            <EngagementCharts
              assessmentId={assessmentId}
              workId={workId}
              students={selectedMembers}
            />
          </Section>
        </div>
      </div>
    </>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  rightHeader?: React.ReactNode;
}
function Section({ title, children, rightHeader }: SectionProps) {
  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <Text kind="headingFive">{title}</Text>
        {rightHeader}
      </div>
      <Sentry.ErrorBoundary
        fallback={
          <div className={styles.emptyContainer}>
            <Text color="grey300" kind="bodyLg" textAlign="center">
              Error loading this graph
            </Text>
          </div>
        }
      >
        {children}
      </Sentry.ErrorBoundary>
    </div>
  );
}

export function EmptyInsightsPage() {
  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <img src={insightsImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          You’ll see insights into group-level engagement here once you start
          working
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Use our learning analytics to drive student engagement and provide
          support
        </Text>
      </div>
    </div>
  );
}
